import { useState, useEffect } from 'react';
import { Box, Alert, AlertIcon, Button, HStack, Text, useToast } from '@chakra-ui/react';
import { ArrowClockwise, Lightning, Database } from '@phosphor-icons/react';
import { checkElasticsearchHealth } from '../utils/elasticsearchHealth';
import MapElastic from './map-elastic';
import Map from './Map';
import ElasticsearchFallback from '../components/ElasticsearchFallback';

/**
 * Smart Map component that defaults to Elasticsearch with fallback to normal map
 */
export default function SmartMap() {
  const [useElastic, setUseElastic] = useState(null); // null = checking, true = elastic, false = normal
  const [isHealthy, setIsHealthy] = useState(null);
  const [manualOverride, setManualOverride] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const toast = useToast();

  // Initial health check
  useEffect(() => {
    const performHealthCheck = async () => {
      try {
        const healthy = await checkElasticsearchHealth();
        setIsHealthy(healthy);
        setUseElastic(healthy);
      } catch (error) {
        console.error('Health check failed:', error);
        setIsHealthy(false);
        setUseElastic(false);
      }
    };

    performHealthCheck();
  }, []);

  // Manual health recheck
  const recheckHealth = async () => {
    setIsChecking(true);
    try {
      const healthy = await checkElasticsearchHealth(true); // Force check
      setIsHealthy(healthy);
      
      if (!manualOverride) {
        setUseElastic(healthy);
      }
      
      toast({
        title: healthy ? 'Elasticsearch is healthy' : 'Elasticsearch is unavailable',
        description: healthy ? 'Connected to enhanced map' : 'Using standard map',
        status: healthy ? 'success' : 'warning',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Manual health check failed:', error);
      setIsHealthy(false);
      if (!manualOverride) {
        setUseElastic(false);
      }
    } finally {
      setIsChecking(false);
    }
  };

  // Manual override to force a specific map type
  const handleManualToggle = (forceElastic) => {
    setManualOverride(true);
    setUseElastic(forceElastic);
    
    toast({
      title: `Switched to ${forceElastic ? 'Enhanced' : 'Standard'} Map`,
      description: forceElastic 
        ? 'Using Elasticsearch for advanced map features' 
        : 'Using standard database map',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  // Reset to automatic mode
  const resetToAuto = () => {
    setManualOverride(false);
    setUseElastic(isHealthy);
    
    toast({
      title: 'Switched to Automatic Mode',
      description: 'Map type will be determined by system availability',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  // Show loading state while checking
  if (useElastic === null) {
    return (
      <Box p={4}>
        <Alert status="info">
          <AlertIcon />
          Checking map system availability...
        </Alert>
      </Box>
    );
  }

  // Show fallback component if Elasticsearch is not healthy and not manually overridden
  if (useElastic === false && !manualOverride) {
    return (
      <ElasticsearchFallback 
        featureName="Map"
        onSwitch={() => handleManualToggle(false)} 
      />
    );
  }

  return (
    <Box>
      {/* Render the appropriate map component */}
      {useElastic ? <MapElastic /> : <Map />}
    </Box>
  );
}
